import {
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";
import { Box, CircularProgress } from "@mui/material";
import { Formik } from "formik";
import { isEqual } from "lodash-es";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import type {
  FormContextData,
  MhFormContext,
  PatientTimelineResponse,
} from "@thalamos/aspire-common";
import { getBaseFormTemplate } from "@thalamos/aspire-common";

import { ConfirmationModal } from "../ConfirmationModal.js";
import { safeCast } from "../FormDraft/helpers/safeCast.js";
import { ThalamosForm } from "../FormDraft/ThalamosForm.js";
import type { SectionName } from "../FormProgressPage/soad-assessment/soadAssessmentLogic.js";
import { soadAssessmentLogic } from "../FormProgressPage/soad-assessment/soadAssessmentLogic.js";
import { useSoadNotesDrawer } from "../FormProgressPage/soad-assessment/SoadNotesDrawer.js";
import { api, apiHooks } from "~/api.js";
import { Banner, FormTitle } from "~/components/design-system/index.js";
import { TortusDictationProvider } from "~/components/design-system/TortusAvtModal/index.js";
import { FormFooterSection } from "~/components/form/FormFooter.js";
import { PatientBanner } from "~/components/layout/index.js";
import { LoggedInUserContext } from "~/Contexts.js";
import { usePatientTimeline } from "~/hooks/apiCalls.js";
import {
  autoSaveStateReducer,
  initalAutoSaveState,
} from "~/reducers/autoSaveReducer.js";
import { routeFns } from "~/routePaths.js";

export type SoadReportFormCompletionProps = {};

function SoadReportFormCompletionInner({
  formContext,
  sectionName,
  patientTimeline,
  reloadPatientTimeline,
}: {
  formContext: FormContextData;
  sectionName: SectionName;
  patientTimeline: PatientTimelineResponse;
  reloadPatientTimeline: () => void;
}) {
  const userContext = useContext(LoggedInUserContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);

  const [autoSaveState, dispatchAutoSaveState] = useReducer(
    autoSaveStateReducer,
    initalAutoSaveState,
  );

  const {
    soadReportForm: { sections },
    soadNotes: { draft: soadNotesDraft },
    certificates: { forms: state },
    allSrfSectionsMarkedComplete,
  } = soadAssessmentLogic(
    formContext,
    userContext!.user,
    navigate,
    formContext?.activeTeamworkWorkItem !== undefined,
  );

  const srfSection = sections.find(
    (section) => section.sectionName === sectionName,
  );

  const completionFormSection = state.find(
    (f) => f.draft && f.draft.formTemplate.id === sectionName,
  );

  const section = srfSection ?? completionFormSection;

  const formTemplate = getBaseFormTemplate(section?.draft!);
  const partTemplate = formTemplate?.parts[section?.draft!.part! - 1];

  const onSaveSoadNotes = useCallback(
    async (notes: string) => {
      if (!soadNotesDraft?.id) {
        return;
      }
      await api.drafts.updateData(soadNotesDraft.id, {
        notes,
        completionStatus: "completed",
      });
    },
    [soadNotesDraft],
  );

  const soadNotesDrawer = useSoadNotesDrawer({
    initialNotes: soadNotesDraft?.data.notes ?? "",
    onSave: onSaveSoadNotes,
  });

  return (
    <Box sx={{ ...soadNotesDrawer.siblingSx }}>
      <Box sx={{ mb: 3 }}>
        <PatientBanner
          patient={formContext.patient}
          nhsNumber={patientTimeline.patient.nhsNumber ?? undefined}
          disableSticky
          isHorizontalLineHidden
          patientTimeline={patientTimeline}
          reloadPatientTimeline={reloadPatientTimeline}
          isHidden={true}
        />
      </Box>

      <FormTitle
        titleText={section?.title ? t(section.title) : undefined}
        hasTitleBottomMargin={false}
        useReducedTopPadding
      />

      {section && "subHeading" in section && section.subHeading && (
        <FormTitle
          subtitleText={t(section.subHeading)}
          hasTitleBottomMargin={false}
          useReducedTopPadding
        />
      )}

      {section && "banner" in section && section.banner ? (
        <Banner
          bannerType={section.banner.type}
          title={section.banner.bannerText ?? ""}
          sx={{ mb: 3 }}
        />
      ) : null}

      {section?.draft && (
        <Formik
          initialValues={section?.draft!.data}
          onSubmit={async (values: any) => {
            if (
              formTemplate?.id !== "soad-report-form" &&
              !allSrfSectionsMarkedComplete
            ) {
              setShowModal(true);
            } else {
              await api.drafts.updateData(
                section?.draft!.id!,
                safeCast(partTemplate!.dataSchema, {
                  ...values,
                  completionStatus: "completed",
                }),
              );
              navigate(
                routeFns.formContextPage(
                  formContext.id,
                  formContext.patient.id,
                ),
              );
            }
          }}
          validationSchema={partTemplate!.dataSchema}
        >
          {({
            values,
            errors,
            touched,
            setValues,
            handleBlur,
            isSubmitting,
            setFieldTouched,
            submitForm,
            submitCount,
            /* and other goodies */
          }) => {
            const mhFormContext: MhFormContext = {
              // Put the SRF draft data into the context so it can
              // be accessed by the certificates
              previousPartsFormData: sections.map((section) => ({
                section: section.sectionName,
                data: section.draft?.data,
              })),
              previousForms: [],
              linkedForms: [],
              patient: formContext.patient,
            };

            return (
              <>
                <TortusDictationProvider>
                  <ThalamosForm
                    partTemplate={partTemplate!}
                    fieldProps={{
                      validationSchema: partTemplate!.dataSchema,
                      context: mhFormContext,
                      values,
                      setValues: (newValues: any) => {
                        if (!isEqual(values, newValues)) {
                          if (!isEqual(newValues, section?.draft!.data)) {
                            setValues({
                              ...newValues,
                              completionStatus: "in-progress",
                            });
                          } else {
                            setValues(newValues);
                          }
                        }
                      },
                      errors,
                      touched,
                      handleBlur,
                      setFieldTouched,
                    }}
                    submitCount={submitCount}
                    isSubmitting={isSubmitting}
                    isAutoSaving={autoSaveState.isLoading}
                    dispatchAutoSaveState={dispatchAutoSaveState}
                    formDraft={section.draft!}
                    redirectToFormPage={() =>
                      navigate(
                        routeFns.formContextPage(
                          formContext.id,
                          formContext.patient.id,
                        ),
                      )
                    }
                  />
                </TortusDictationProvider>
                <FormFooterSection
                  isSticky={true}
                  disableSubmit={autoSaveState.isLoading || isSubmitting}
                  saveLabel={t("buttonLabels.markComplete")}
                  discardLabel={t("buttonLabels.goBack")}
                  autoSaveState={autoSaveState}
                  onSave={submitForm}
                  onCancel={async () => {
                    // See if the data has changed, ignoring the confirmation status
                    const updated = !isEqual(
                      { ...values, confirmationStatus: "" },
                      { ...section?.draft!.data, confirmationStatus: "" },
                    );

                    if (updated) {
                      await api.drafts.updateData(
                        section?.draft!.id!,
                        safeCast(partTemplate!.dataSchema, {
                          ...values,
                          completionStatus: updated
                            ? "in-progress"
                            : section?.draft!.data.completionStatus,
                        }),
                      );
                    }

                    navigate(
                      routeFns.formContextPage(
                        formContext.id,
                        formContext.patient.id,
                      ),
                    );
                  }}
                >
                  {soadNotesDrawer.Component}
                </FormFooterSection>
              </>
            );
          }}
        </Formik>
      )}

      {showModal && (
        <ConfirmationModal
          message={t("components.certificateModal.message")}
          confirmFn={() => {
            setShowModal(false);
          }}
          closeFn={() => setShowModal(false)}
        />
      )}
    </Box>
  );
}

export function SoadReportFormCompletion({}: SoadReportFormCompletionProps) {
  const { formContextId, patientId, sectionName } = useParams();
  const navigate = useNavigate();

  const [{ data: form, loading: formLoading, response: formResponse }] =
    apiHooks.forms.getFormContext(formContextId!);

  useEffect(() => {
    if (!formLoading) {
      if (formResponse?.status === 404) {
        navigate(routeFns.notFound(), { replace: true });
      }
    }
  }, [form, formLoading, formResponse?.status]);

  const { patientTimeline, reloadPatientTimeline } = usePatientTimeline({
    patientId,
  });

  return !form || !patientTimeline ? (
    <CircularProgress />
  ) : (
    <SoadReportFormCompletionInner
      formContext={form}
      patientTimeline={patientTimeline}
      reloadPatientTimeline={reloadPatientTimeline}
      sectionName={sectionName as SectionName}
    />
  );
}
